import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChartLine,
  FaUsers,
  FaHeart,
  FaEye,
  FaArrowRight,
  FaWhatsapp,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFileAlt
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { fetchPortfolio, resolveImageUrl } from '../api';

const categoryColors = {
  'Logo': 'from-pink-500 to-rose-600',
  'Resume': 'from-green-500 to-emerald-600',
  'Website': 'from-indigo-500 to-purple-600',
  'Invitation E-Card': 'from-pink-500 to-rose-600',
  'Banners': 'from-blue-500 to-indigo-600',
  'Pamphlet': 'from-amber-500 to-orange-600',
  'Poster': 'from-red-500 to-orange-600',
  'Social Media': 'from-pink-500 to-rose-600',
  'Visiting Card': 'from-slate-500 to-gray-600',
};

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [slide, setSlide] = useState(0);
  const [projects, setProjects] = useState([]);

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to discuss a project similar to your portfolio work.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchPortfolio({ status: 'active' });
        // live API data only — placeholder rendered when an item has no uploaded image
        setProjects(data.map((item) => {
          const img = item.featuredImage ? resolveImageUrl(item.featuredImage) : null;
          return {
            id: item._id,
            title: item.title,
            client: item.client || 'Sky Muse',
            category: item.category,
            image: img,
            gallery: (item.galleryImages || []).map(resolveImageUrl),
            description: item.description,
            results: [
              { label: 'Growth', value: 'Dynamic', icon: FaChartLine },
              { label: 'Performance', value: 'Live', icon: FaHeart },
            ],
            services: [item.category || 'Design'],
            color: categoryColors[item.category] || 'from-purple-500 to-indigo-600',
          };
        }));
      } catch (err) {
        console.warn('Using fallback portfolio data');
        setProjects([]);
      }
    };
    load();
  }, []);

  const filters = ['All', 'Logo', 'Visiting Card', 'Social Media', 'Poster', 'Pamphlet', 'Website', 'Banners', 'Resume', 'Invitation E-Card'];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  const handleCardClick = (project, e) => {
    const imgEl = e.currentTarget.querySelector('img');
    const src = imgEl ? imgEl.getAttribute('src') : project.image;
    setSelectedImageSrc(src);
    setSlide(0);
    setSelectedProject(project);
  };

  // gallery slider when the item has gallery images, otherwise just the featured image
  const modalImages = selectedProject
    ? (selectedProject.gallery?.length
        ? selectedProject.gallery
        : (selectedImageSrc || selectedProject.image ? [selectedImageSrc || selectedProject.image] : []))
    : [];
  const prevSlide = (e) => { e.stopPropagation(); setSlide((s) => (s - 1 + modalImages.length) % modalImages.length); };
  const nextSlide = (e) => { e.stopPropagation(); setSlide((s) => (s + 1) % modalImages.length); };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-1/2 -left-1/2 w-full h-full"
          >
            <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
            <div className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px]" />
          </motion.div>

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <HiSparkles className="text-yellow-400 text-lg" />
              <span className="text-white font-semibold text-sm">Our Work</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                Portfolio
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto"
            >
              Explore our successful projects and see how we've helped businesses achieve remarkable digital growth
            </motion.p>

            {/* Stats Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex justify-center gap-8 mt-10 mb-16"
            >
              {[
                { value: '100+', label: 'Projects' },
                { value: '50+', label: 'Happy Clients' },
                { value: '300%', label: 'Avg. Growth' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-16">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Portfolio Filter & Grid */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-100/50 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {filters.map((filter) => (
              <motion.button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 cursor-pointer ${activeFilter === filter
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {filter}
              </motion.button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ y: -10 }}
                  onClick={(e) => handleCardClick(project, e)}
                  className="group cursor-pointer"
                >
                  <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 group-hover:shadow-2xl group-hover:border-transparent transition-all duration-500">
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      {project.image ? (
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${project.color} flex items-center justify-center`}>
                          <span className="text-white/40 text-7xl font-bold select-none">{project.title?.charAt(0)}</span>
                        </div>
                      )}
                      {/* Overlay */}
                      {!(project.category === 'Social Media' && (project.id === 1 || project.id === 7)) && (
                        <>
                          <div className={`absolute inset-0 bg-gradient-to-t ${project.color} opacity-0 group-hover:opacity-80 transition-opacity duration-300`} />
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileHover={{ opacity: 1, y: 0 }}
                            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                              <FaEye className="text-slate-900 text-xl" />
                            </div>
                          </motion.div>
                        </>
                      )}

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className={`px-4 py-1.5 bg-gradient-to-r ${project.color} text-white text-xs font-semibold rounded-full shadow-lg`}>
                          {project.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600">
                        {project.title}
                      </h3>
                      <p className="text-slate-500 text-sm mb-4">{project.client}</p>

                      {/* Quick Results */}
                      <div className="flex items-center gap-4">
                        {project.results.slice(0, 2).map((result, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <result.icon className="text-slate-400 text-sm" />
                            <span className="text-sm font-semibold text-slate-700">{result.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative flex items-center justify-center"
            >
              {modalImages.length > 0 ? (
                <div className="relative w-full flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={slide}
                      src={modalImages[slide]}
                      alt={`${selectedProject.title} ${slide + 1}`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.25 }}
                      className="block max-w-full max-h-[90vh] object-contain m-0"
                    />
                  </AnimatePresence>

                  {modalImages.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
                      >
                        <FaChevronLeft className="text-slate-900" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
                      >
                        <FaChevronRight className="text-slate-900" />
                      </button>

                      {/* counter + dots */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                        <div className="flex gap-2">
                          {modalImages.map((_, i) => (
                            <button
                              key={i}
                              onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                              className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${i === slide ? 'bg-white scale-110 shadow' : 'bg-white/50 hover:bg-white/80'}`}
                            />
                          ))}
                        </div>
                        <span className="px-3 py-1 bg-black/50 text-white text-xs font-medium rounded-full">
                          {slide + 1} / {modalImages.length}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={`w-full py-24 bg-gradient-to-br ${selectedProject.color} flex flex-col items-center justify-center rounded-3xl`}>
                  <span className="text-white text-3xl font-bold mb-3">{selectedProject.title}</span>
                  <p className="text-white/80 max-w-lg text-center px-6">{selectedProject.description}</p>
                </div>
              )}
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
              >
                <FaTimes className="text-slate-900" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full border border-blue-500/20 mb-8"
            >
              <HiSparkles className="text-blue-600" />
              <span className="text-blue-700 font-medium">Your Success Story Awaits</span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Be Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Next Success?
              </span>
            </h2>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              Let's create something amazing together and add your story to our portfolio
            </p>

            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-full shadow-2xl transition-all cursor-pointer group"
            >
              <FaWhatsapp className="text-2xl" />
              Start Your Project
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
              </motion.span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
